import { ApplicationStatus, CategoryTypes } from "../../../interfaces/enums";
import { ApplicationWithId } from "../../../interfaces/IApplications";
import {
  Box,
  Button,
  IconButton,
  Tooltip,
  Typography
  } from "@mui/material";
import { CategoryWithId } from "../../../interfaces/ICategories";
import { getCategoryAgeRangeShortDescription } from "../../../utils/dates";
import { getCategoryTypeLabel } from "../../../utils/categoryTypeTranslations";
import {
  Add,
  Cake,
  EmojiEvents,
  ExpandLess,
  ExpandMore,
  Group,
  Slideshow,
  Edit,
} from "@mui/icons-material";

interface CategoryHeaderProps {
  category: CategoryWithId;
  application: ApplicationWithId;
  participantsInCategory: number;
  categoryPresentationsCount: number;
  expanded: boolean;
  canAddPresentation: boolean;
  onAddPresentation: (categoryId: string) => void;
  onToggleExpand: () => void;
}

export default function CategoryHeader({
  category,
  application,
  participantsInCategory,
  categoryPresentationsCount,
  expanded,
  canAddPresentation,
  onAddPresentation,
  onToggleExpand,
}: CategoryHeaderProps) {
  return (
    <Box
      sx={{
        p: 2,
        cursor: "pointer",
        "&:hover": { backgroundColor: "#f9f9f9" },
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
      }}
      onClick={onToggleExpand}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, width: "100%" }}>
        {/* Nazwa kategorii */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flex: "1 1 auto" }}>
          <EmojiEvents sx={{ color: "#6366f1", fontSize: 20 }} />
          <Typography variant="h6" sx={{ fontWeight: 600, color: "#2f2f2f" }}>
            {category.name}
          </Typography>
        </Box>

        {/* Typ kategorii */}
        <Typography variant="body2" sx={{ color: "#6c6c6c", minWidth: "120px" }}>
          {getCategoryTypeLabel(category.type)}
        </Typography>

        {/* Przedział wiekowy - ukryty */}
        {/* <Tooltip title={getCategoryAgeRangeShortDescription(category)} arrow>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: "150px" }}>
            <Cake sx={{ fontSize: 16, color: "#6c6c6c" }} />
            <Typography
              variant="body2"
              sx={{
                color: "#2f2f2f",
                fontSize: "0.875rem",
              }}
            >
              {getCategoryAgeRangeShortDescription(category)}
            </Typography>
          </Box>
        </Tooltip> */}

        {/* Ilość zawodników */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: "80px" }}>
          <Group sx={{ fontSize: 16, color: "#6c6c6c" }} />
          <Typography
            variant="body2"
            sx={{
              fontWeight: participantsInCategory > 0 ? 700 : 400,
              color: "#2f2f2f",
            }}
          >
            {participantsInCategory}
          </Typography>
        </Box>

        {/* Ilość prezentacji */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: "80px" }}>
          <Slideshow sx={{ fontSize: 16, color: "#6c6c6c" }} />
          <Typography
            variant="body2"
            sx={{
              fontWeight: categoryPresentationsCount > 0 ? 700 : 400,
              color: "#2f2f2f",
            }}
          >
            {categoryPresentationsCount}
          </Typography>
        </Box>

        {/* Przycisk dodawania/edycji prezentacji - ukryty gdy rejestracja zamknięta */}
        {canAddPresentation && (
          <Button
            variant="contained"
            size="small"
            startIcon={
              category.type === CategoryTypes.SOLO && categoryPresentationsCount > 0 ? (
                <Edit />
              ) : (
                <Add />
              )
            }
            onClick={(e) => {
              e.stopPropagation();
              onAddPresentation(category.id!);
            }}
            sx={{
              backgroundColor: category.type === CategoryTypes.SOLO ? "#3b82f6" : "#10b981",
              "&:hover": {
                backgroundColor: category.type === CategoryTypes.SOLO ? "#2563eb" : "#059669",
              },
              flexShrink: 0,
            }}
          >
            {category.type === CategoryTypes.SOLO
              ? categoryPresentationsCount > 0
                ? "Edytuj prezentacje"
                : "Dodaj prezentacje"
              : "Dodaj prezentację"}
          </Button>
        )}

        {/* Przycisk rozwijania */}
        <IconButton size="small" sx={{ flexShrink: 0 }}>
          {expanded ? <ExpandLess /> : <ExpandMore />}
        </IconButton>
      </Box>
    </Box>
  );
}
